import { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import PageHeader from '@/components/PageHeader';
import Pagination from '@/components/Pagination';
import ProgramQualificationList from '@/components/ProgramQualificationList';
import usePagination from '@/hooks/usePagination';
import useProgramQualifications from '@/hooks/useProgramQualifications';
import PersonContext from '@/pages/Person/PersonContext';
import Alert from '@/ui/components/Alert';
import Container from '@/ui/components/Container';
import LoadingSkeleton from '@/ui/components/LoadingSkeleton';

const PersonProgramQualifications = () => {
  const { person } = useContext(PersonContext);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [searchParams, setSearchParams] = useSearchParams({ page: '1' });

  const { currentPage, goToPage, nextPage, prevPage } = usePagination(
    +(searchParams.get('page') ?? '1'),
    totalPages
  );

  useEffect(() => {
    setSearchParams((currentParams) => {
      currentParams.set('page', currentPage.toString());
      return currentParams;
    });
  }, [currentPage, setSearchParams]);

  const {
    data: programQualifications,
    isError,
    error,
    isPending,
    isSuccess
  } = useProgramQualifications({
    person_id: person.id,
    page: currentPage.toString()
  });

  useEffect(() => {
    if (isSuccess) {
      if (programQualifications?.data?.page) {
        setSearchParams((currentParams) => {
          currentParams.set('page', programQualifications.data!.page.toString());
          return currentParams;
        });
      }

      if (programQualifications?.data?.total_pages) {
        setTotalPages(programQualifications.data.total_pages);
      }
    }
  }, [isSuccess, programQualifications, setSearchParams]);

  return (
    <Container type="page">
      <PageHeader headingText="Program Qualifications" className="sr-only" />

      <Pagination
        currentPage={currentPage}
        goToPage={goToPage}
        nextPage={nextPage}
        prevPage={prevPage}
        totalPages={totalPages}>
        {isPending ? (
          <LoadingSkeleton />
        ) : isError ? (
          <Alert level="error">{error.toString()}</Alert>
        ) : programQualifications?.data?.results.length ? (
          <ProgramQualificationList programQualifications={programQualifications.data.results} />
        ) : (
          <div>No program qualifications found.</div>
        )}
      </Pagination>
    </Container>
  );
};

export default PersonProgramQualifications;
